import ImageTracer from 'imagetracerjs'

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = URL.createObjectURL(file)
  })
}

function getImageData(img, maxSize = 1200) {
  let { width, height } = img
  const scale = Math.min(1, maxSize / Math.max(width, height))
  width = Math.round(width * scale)
  height = Math.round(height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  ctx.drawImage(img, 0, 0, width, height)
  return { imageData: ctx.getImageData(0, 0, width, height), width, height }
}

function detectBackgroundColor(imageData, width, height) {
  const { data } = imageData
  const corners = [
    0,
    (width - 1) * 4,
    (height - 1) * width * 4,
    ((height - 1) * width + (width - 1)) * 4,
  ]
  const sums = corners.reduce(
    (acc, offset) => {
      acc[0] += data[offset]
      acc[1] += data[offset + 1]
      acc[2] += data[offset + 2]
      return acc
    },
    [0, 0, 0]
  )
  return {
    r: Math.round(sums[0] / corners.length),
    g: Math.round(sums[1] / corners.length),
    b: Math.round(sums[2] / corners.length),
  }
}

function applyBackgroundRemoval(imageData, width, height, tolerance = 32) {
  const bg = detectBackgroundColor(imageData, width, height)
  const { data } = imageData
  for (let i = 0; i < data.length; i += 4) {
    const dr = data[i] - bg.r
    const dg = data[i + 1] - bg.g
    const db = data[i + 2] - bg.b
    const dist = Math.sqrt(dr * dr + dg * dg + db * db)
    if (dist <= tolerance) data[i + 3] = 0
  }
  return bg
}

function applyGrayscale(imageData) {
  const { data } = imageData
  for (let i = 0; i < data.length; i += 4) {
    const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
    data[i] = data[i + 1] = data[i + 2] = lum
  }
}

function applyBlackAndWhite(imageData, threshold = 128) {
  const { data } = imageData
  for (let i = 0; i < data.length; i += 4) {
    const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
    const v = lum >= threshold ? 255 : 0
    data[i] = data[i + 1] = data[i + 2] = v
  }
}

const COLOR_MODE_SETTINGS = {
  color: { numberofcolors: 16 },
  reduced: { numberofcolors: 6 },
  grayscale: { numberofcolors: 8 },
  bw: { numberofcolors: 2 },
}

// Maps a 0-100 "detail" slider to imagetracerjs tracing precision.
function detailToTraceParams(detail) {
  const t = Math.min(100, Math.max(0, detail)) / 100
  return {
    ltres: 3 - t * 2.7, // 3.0 (coarse) -> 0.3 (fine)
    qtres: 3 - t * 2.7,
    pathomit: Math.round(30 - t * 27), // 30 (coarse) -> 3 (fine)
  }
}

/**
 * @param {File} file
 * @param {Object} options
 * @param {'color'|'reduced'|'grayscale'|'bw'} options.colorMode
 * @param {number} options.detail - 0 (simplified) to 100 (max detail)
 * @param {boolean} options.transparentBackground
 * @param {number} options.backgroundTolerance
 */
export async function traceImageToSVG(file, options = {}) {
  const {
    colorMode = 'color',
    detail = 55,
    transparentBackground = false,
    backgroundTolerance = 32,
  } = options

  const img = await loadImage(file)
  const { imageData, width, height } = getImageData(img)

  if (colorMode === 'grayscale') applyGrayscale(imageData)
  if (colorMode === 'bw') applyBlackAndWhite(imageData)

  let backgroundColor = null
  if (transparentBackground) {
    backgroundColor = applyBackgroundRemoval(imageData, width, height, backgroundTolerance)
  }

  const traceOptions = {
    ...detailToTraceParams(detail),
    ...COLOR_MODE_SETTINGS[colorMode],
    width,
    height,
    strokewidth: 0,
    scale: 1,
  }

  let svg = ImageTracer.imagedataToSVG(imageData, traceOptions)
  svg = svg.replace(
    /<svg[^>]*>/,
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">`
  )

  return { svg, width, height, backgroundColor }
}

export function withSolidBackground(svg, width, height, color) {
  const rect = `<rect x="0" y="0" width="${width}" height="${height}" fill="${color}" />`
  return svg.replace(/(<svg[^>]*>)/, `$1${rect}`)
}
